import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaRegEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { Icon } from "organisms/iconCommonent";
import { ApiContent } from "./shaping";

interface Selection {
  _id?: string;
  title: string;
  features: string[];
}

export default function SelectionSection() {
  const [selections, setSelections] = useState<Selection[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSelection, setEditingSelection] = useState<Selection | null>(null);
  const [apiContent, setApiContent] = useState<ApiContent | null>(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editHeading, setEditHeading] = useState("");
  const [editDescription, setEditDescription] = useState("");

  // Fetch selections + heading/description
  const fetchSelections = async () => {
    try {
      const [selectionRes, contentRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_LOCAL_URL}/api/selection`),
        axios.get(`${process.env.REACT_APP_LOCAL_URL}/api/content/68efce242d25c3fab3031a40`)
      ]);

      if (contentRes.data.status === "success") setApiContent(contentRes.data.data);
      if (selectionRes.data.status === "success") setSelections(selectionRes.data.data);
    } catch (error) {
      console.error("Error fetching selections:", error);
    }
  };

  useEffect(() => {
    fetchSelections();
  }, []);

  // Delete selection
  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`${process.env.REACT_APP_LOCAL_URL}/api/selection/${id}`);
      fetchSelections();
    } catch (error) {
      console.error("Error deleting selection:", error);
    }
  };

  // Open selection form modal
  const handleEditSelection = (item?: Selection) => {
    setEditingSelection(item || null);
    setShowForm(true);
  };

  // Open edit heading popup
  const handleEditHeadingPopup = () => {
    if (apiContent) {
      setEditHeading(apiContent.heading || "");
      setEditDescription(apiContent.description || "");
      setShowEditPopup(true);
    }
  };

  // Save updated heading/description
  const handleSaveHeading = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.patch(`${process.env.REACT_APP_LOCAL_URL}/api/content/68efce242d25c3fab3031a40`, {
        heading: editHeading,
        description: editDescription,
      });
      setShowEditPopup(false);
      fetchSelections();
    } catch (error) {
      console.error("Error updating heading:", error);
    }
  };

  return (
    <section className="pt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="">Section - 08</h2>
        <button
          onClick={handleEditHeadingPopup}
          className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-md"
        >
          <Icon icon={FaRegEdit} />
          Edit Heading
        </button>
      </div>

      {/* Heading & Description */}
      {apiContent && (
        <div className="mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <h3 className="font-lato-bold text-xl mb-2">{apiContent.heading}</h3>
          <p className="text-gray-700">{apiContent.description}</p>
        </div>
      )}

      <h3 className="font-lato-regular my-4">From selection to impact</h3>

      {/* Selections Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 bg-white border p-4 border-gray-200 rounded-xl overflow-hidden">
        {selections.map((item) => (
          <div
            key={item._id}
            className="bg-white p-4 border border-gray-300 rounded-lg shadow-md flex flex-col justify-between"
          >
            <div>
              <h4 className="font-lato-bold text-lg mb-2">{item.title}</h4>
              <ul className="list-disc pl-5 space-y-1">
                {item.features.map((feat, i) => (
                  <li key={i}>{feat}</li>
                ))}
              </ul>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => handleEditSelection(item)}
                className="bg-blue-500 text-white px-3 py-1 rounded flex items-center gap-1"
              >
                <Icon icon={FaRegEdit} />
              </button>
              <button
                onClick={() => handleDelete(item._id!)}
                className="text-red-500 border border-red-500 px-3 py-1 rounded flex items-center gap-1"
              >
                <Icon icon={MdDelete} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Selection Modal */}
      {showForm && (
        <SelectionFormModal
          onClose={() => setShowForm(false)}
          fetchSelections={fetchSelections}
          editingSelection={editingSelection}
        />
      )}

      {/* Heading Edit Popup */}
      {showEditPopup && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
            <h3 className="text-lg font-lato-bold mb-4">Edit Heading & Description</h3>
            <form onSubmit={handleSaveHeading} className="space-y-4">
              <div>
                <label className="block font-lato-bold mb-1">Heading</label>
                <input
                  type="text"
                  value={editHeading}
                  onChange={(e) => setEditHeading(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
              </div>

              <div>
                <label className="block font-lato-bold mb-1">Description</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2 min-h-[100px]"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditPopup(false)}
                  className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

// ========================= FORM MODAL =========================

interface FormProps {
  onClose: () => void;
  fetchSelections: () => void;
  editingSelection: Selection | null;
}

const SelectionFormModal = ({
  onClose,
  fetchSelections,
  editingSelection,
}: FormProps) => {
  const [title, setTitle] = useState(editingSelection?.title || "");
  const [features, setFeatures] = useState<string[]>(editingSelection?.features || [""]);

  const handleFeatureChange = (index: number, value: string) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setFeatures(newFeatures);
  };

  const addFeature = () => setFeatures([...features, ""]);
  const removeFeature = (index: number) => {
    const newFeatures = features.filter((_, i) => i !== index);
    setFeatures(newFeatures);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = { title, features };
    try {
      if (editingSelection) {
        await axios.put(
          `${process.env.REACT_APP_LOCAL_URL}/api/selection/${editingSelection._id}`,
          payload
        );
      } else {
        await axios.post(`${process.env.REACT_APP_LOCAL_URL}/api/selection`, payload);
      }

      fetchSelections();
      onClose();
    } catch (error) {
      console.error("Error saving selection:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-xl w-[500px] shadow-lg">
        <h3 className="text-lg font-lato-bold mb-4">
          {editingSelection ? "Edit Selection" : "Add Selection"}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-lato-bold mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
              required
            />
          </div>

          <div>
            <label className="block font-lato-bold mb-1">Features</label>
            {features.map((feat, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={feat}
                  onChange={(e) => handleFeatureChange(index, e.target.value)}
                  className="w-full border border-gray-300 rounded-md p-2"
                  required
                />
                {features.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="text-red-500"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addFeature}
              className="text-blue-500 text-sm mt-1"
            >
              + Add Feature
            </button>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md"
            >
              {editingSelection ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-red-500 border border-red-500 px-4 py-2 rounded-md hover:bg-red-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
